import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Button from '../../components/Button/Button';
import { createStripeCheckoutSession } from '../../lib/stripeFunctions';
import { getFlightOffer, summarizeFlightOffer } from '../../lib/duffelFlightsApi';
import './FlightBooking.css';

const emptyPassengerRow = () => ({
  title: 'mr',
  gender: 'm',
  given_name: '',
  family_name: '',
  born_on: '',
  email: '',
  phone_number: '',
});

const fmtShortTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const FlightBooking = ({ onGoHome, onNavigate }) => {
  const { formatFrom } = useCurrency();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const offerIdFromQuery = searchParams.get('offer');
  const offerIdFromState = location.state?.offerId;
  const offerId = offerIdFromState || offerIdFromQuery;

  const [loadingOffer, setLoadingOffer] = useState(true);
  const [offerError, setOfferError] = useState('');
  const [offer, setOffer] = useState(null);

  const [passengersForm, setPassengersForm] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!offerId) {
        setOfferError('Missing flight offer. Go back to flights search.');
        setLoadingOffer(false);
        return;
      }
      setLoadingOffer(true);
      setOfferError('');
      try {
        const res = await getFlightOffer(offerId);
        const o = res?.data;
        if (cancelled) return;
        if (!o) {
          setOfferError('Offer could not be loaded.');
          return;
        }
        setOffer(o);
        const pas = Array.isArray(o.passengers) ? o.passengers : [];
        setPassengersForm(
          pas.map(() => ({
            ...emptyPassengerRow(),
          })),
        );
      } catch (e) {
        if (!cancelled) {
          setOfferError(e?.message || 'Failed to load offer');
        }
      } finally {
        if (!cancelled) setLoadingOffer(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [offerId]);

  const summary = useMemo(() => summarizeFlightOffer(offer), [offer]);

  const updatePassenger = (index, field, value) => {
    setPassengersForm((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBookingError('');
    setSubmitting(true);

    try {
      const pasList = offer?.passengers || [];
      if (pasList.length !== passengersForm.length) {
        throw new Error('Passenger count mismatch — reload this page.');
      }
      const passengersPayload = pasList.map((p, i) => ({
        id: p.id,
        title: passengersForm[i].title,
        gender: passengersForm[i].gender,
        given_name: passengersForm[i].given_name.trim(),
        family_name: passengersForm[i].family_name.trim(),
        born_on: passengersForm[i].born_on,
        email: passengersForm[i].email.trim(),
        phone_number: passengersForm[i].phone_number.trim(),
      }));

      const resumeKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `pay_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

      sessionStorage.setItem(
        `tourasco_pay_${resumeKey}`,
        JSON.stringify({
          type: 'flight',
          offerId,
          passengers: passengersPayload,
        }),
      );

      const routeRows = summary?.slices || [];
      const routeTitle =
        routeRows.length > 0
          ? routeRows.map((r) => `${r.origin}–${r.dest}`).join(', ')
          : 'Flight booking';

      const amount = Number(offer.total_amount);
      const currency = String(offer.total_currency || 'gbp').toLowerCase();
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Invalid price for checkout.');
      }

      const checkoutUrl = await createStripeCheckoutSession({
        amount,
        currency,
        hotelName: `Flight · ${routeTitle}`,
        propertyId: offerId,
        resumeKey,
        cancelPath: `/flights/book?offer=${encodeURIComponent(offerId)}`,
        successPath: '/flights/booking-confirmation',
      });

      window.location.assign(checkoutUrl);
    } catch (err) {
      setBookingError(err?.message || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flight-booking-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="flight-booking-main">
        <div className="flight-booking-container">
              <nav className="flight-booking-nav" aria-label="Booking steps">
                <Link className="flight-booking-nav__link" to="/flights">
                  ← Flight search
                </Link>
                {offerId ? (
                  <Link className="flight-booking-nav__link flight-booking-nav__link--ghost" to={`/flights/offer/${encodeURIComponent(offerId)}`}>
                    Review itinerary
                  </Link>
                ) : null}
              </nav>

              <header className="flight-booking-page-head">
                <h1 className="flight-booking-title">Complete your booking</h1>
                <p className="flight-booking-lead">
                  Enter traveler names exactly as on passports or ID. Next you&apos;ll go straight to Stripe to pay; after payment we confirm your flights with the airline (Duffel).
                </p>
              </header>

              {loadingOffer ? (
                <div className="flight-booking-loading-card" aria-busy="true">
                  <div className="flight-booking-loading-card__shine" />
                  <div className="flight-booking-skeleton">
                    <div className="flight-booking-skeleton__line flight-booking-skeleton__line--lg" />
                    <div className="flight-booking-skeleton__line" />
                    <div className="flight-booking-skeleton__line flight-booking-skeleton__line--sm" />
                  </div>
                  <p className="flight-booking-loading-text">Loading your fare…</p>
                </div>
              ) : null}

              {!loadingOffer && offerError ? (
                <div className="flight-booking-error">{offerError}</div>
              ) : null}

              {!loadingOffer && offer && summary ? (
                <section className="flight-booking-trip">
                  <div className="flight-booking-trip__accent" aria-hidden />
                  <div className="flight-booking-trip__inner">
                    <div className="flight-booking-trip__header">
                      <h2 className="flight-booking-trip__title">Trip summary</h2>
                      {offer.owner?.name ? (
                        <span className="flight-booking-trip__airline">{offer.owner.name}</span>
                      ) : null}
                    </div>
                    <ul className="flight-booking-trip__routes">
                      {summary.slices?.map((row, idx) => (
                        <li key={`sum-${idx}`} className="flight-booking-trip__leg">
                          <div className="flight-booking-trip__leg-main">
                            <span className="flight-booking-trip__codes">
                              {row.origin}
                              <span className="flight-booking-trip__arrow">→</span>
                              {row.dest}
                            </span>
                            <span
                              className={
                                row.stops === 0
                                  ? 'flight-booking-trip__pill flight-booking-trip__pill--direct'
                                  : 'flight-booking-trip__pill'
                              }
                            >
                              {row.stops === 0 ? 'Direct' : `${row.stops} stop${row.stops === 1 ? '' : 's'}`}
                            </span>
                          </div>
                          <div className="flight-booking-trip__leg-meta">
                            {fmtShortTime(row.dep) ? (
                              <span>
                                Depart <strong>{fmtShortTime(row.dep)}</strong>
                              </span>
                            ) : null}
                            {row.marketing ? <span className="flight-booking-trip__carrier">{row.marketing}</span> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="flight-booking-trip__price">
                      <span className="flight-booking-trip__price-label">Total due</span>
                      <div className="flight-booking-trip__price-row">
                        <span className="flight-booking-trip__amount flight-booking-trip__amount--converted">
                          {formatFrom(offer.total_amount, offer.total_currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
              ) : null}

              {!loadingOffer && offer && passengersForm.length > 0 ? (
                <section className="flight-booking-passengers">
                  <h2 className="flight-booking-passengers__heading">Traveler details</h2>
                  <p className="flight-booking-passengers__sub">
                    {passengersForm.length} traveler{passengersForm.length === 1 ? '' : 's'} · must match travel documents
                  </p>
                  <form className="flight-booking-form" onSubmit={handleSubmit}>
                    {passengersForm.map((row, index) => (
                      <fieldset key={`pass-${index}`} className="flight-booking-fieldset">
                        <legend className="flight-booking-fieldset__legend">
                          <span className="flight-booking-fieldset__num">{index + 1}</span>
                          Traveler {index + 1}
                        </legend>
                        <div className="flight-booking-grid">
                          <label className="flight-booking-field">
                            <span className="flight-booking-field__label">Title</span>
                            <select
                              value={row.title}
                              onChange={(e) => updatePassenger(index, 'title', e.target.value)}
                              required
                            >
                              <option value="mr">Mr</option>
                              <option value="mrs">Mrs</option>
                              <option value="ms">Ms</option>
                              <option value="miss">Miss</option>
                              <option value="dr">Dr</option>
                            </select>
                          </label>
                          <label className="flight-booking-field">
                            <span className="flight-booking-field__label">Gender</span>
                            <select
                              value={row.gender}
                              onChange={(e) => updatePassenger(index, 'gender', e.target.value)}
                              required
                            >
                              <option value="m">M</option>
                              <option value="f">F</option>
                            </select>
                          </label>
                          <label className="flight-booking-field">
                            <span className="flight-booking-field__label">Given name</span>
                            <input
                              value={row.given_name}
                              onChange={(e) => updatePassenger(index, 'given_name', e.target.value)}
                              autoComplete="given-name"
                              required
                            />
                          </label>
                          <label className="flight-booking-field">
                            <span className="flight-booking-field__label">Family name</span>
                            <input
                              value={row.family_name}
                              onChange={(e) => updatePassenger(index, 'family_name', e.target.value)}
                              autoComplete="family-name"
                              required
                            />
                          </label>
                          <label className="flight-booking-field">
                            <span className="flight-booking-field__label">Date of birth</span>
                            <input
                              type="date"
                              value={row.born_on}
                              onChange={(e) => updatePassenger(index, 'born_on', e.target.value)}
                              required
                            />
                          </label>
                          <label className="flight-booking-field">
                            <span className="flight-booking-field__label">Email</span>
                            <input
                              type="email"
                              value={row.email}
                              onChange={(e) => updatePassenger(index, 'email', e.target.value)}
                              autoComplete="email"
                              required
                            />
                          </label>
                          <label className="flight-booking-field flight-booking-field--full">
                            <span className="flight-booking-field__label">Phone (E.164)</span>
                            <input
                              value={row.phone_number}
                              onChange={(e) => updatePassenger(index, 'phone_number', e.target.value)}
                              placeholder="+44 7700 900123"
                              autoComplete="tel"
                              required
                            />
                          </label>
                        </div>
                      </fieldset>
                    ))}

                    {bookingError ? <div className="flight-booking-error">{bookingError}</div> : null}

                    <div className="flight-booking-actions">
                      <Button type="submit" variant="primary" size="lg" disabled={submitting}>
                        {submitting ? 'Opening Stripe checkout…' : 'Continue to payment'}
                      </Button>
                      <p className="flight-booking-actions__note">
                        By confirming you agree your details are accurate for airline ticketing.
                      </p>
                    </div>
                  </form>
                </section>
              ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FlightBooking;
